/**
 * `@aio/platform-table` — public barrel.
 *
 * The default export surface is the high-level {@link DataTable} composer
 * plus the column-definition primitives needed to call it. Every other
 * symbol is exported because parts of the engine (filters, exports, saved
 * views, headers, etc.) are individually useful when callers want to skip
 * the composer and build their own table shell.
 *
 * Internal helpers (e.g. `evaluateFilter` inside `data-table.tsx`) are not
 * re-exported — they're implementation details of the composer.
 */

// ─── Public composer ────────────────────────────────────────────────────────
export {
  DataTable,
  type DataTableProps,
  type BulkAction,
  type SupportedLocale,
} from "./data-table";

// ─── Column system ──────────────────────────────────────────────────────────
export {
  defineColumns,
  resolveAccessor,
  type ColumnDef,
  type ColumnType,
  type ColumnAlign,
  type ColumnSticky,
  type CellRenderArgs,
  type FormatArgs,
} from "./columns";

// ─── Cell formatting ────────────────────────────────────────────────────────
export {
  formatCell,
  EMPTY_DATE_DISPLAY,
  type FormatterBundle,
} from "./lib/format";

// ─── State container ────────────────────────────────────────────────────────
export {
  useTableState,
  TABLE_STATE_STORAGE_PREFIX,
  type TableState,
  type TableStateInit,
  type TableStateSetters,
  type UseTableStateResult,
} from "./use-table-state";

// ─── Virtualization ─────────────────────────────────────────────────────────
export {
  useVirtualRows,
  VIRTUALIZE_THRESHOLD,
  type UseVirtualRowsOptions,
  type UseVirtualRowsResult,
} from "./use-virtual-rows";

// ─── Saved views ────────────────────────────────────────────────────────────
export {
  useSavedViews,
  SAVED_VIEWS_STORAGE_PREFIX,
  type SavedView,
  type UseSavedViewsOptions,
  type UseSavedViewsResult,
} from "./use-saved-views";
export {
  SavedViewsMenu,
  type SavedViewsMenuProps,
} from "./views/saved-views-menu";
export {
  encodeViewToHash,
  decodeHashToView,
  tableStateToListState,
  listStateToTableState,
  type ShareableViewState,
} from "./views/view-state";

// ─── Keyboard navigation ────────────────────────────────────────────────────
export {
  useKeyboardNav,
  type UseKeyboardNavOptions,
  type UseKeyboardNavResult,
} from "./use-keyboard-nav";

// ─── Header ─────────────────────────────────────────────────────────────────
export { TableHeader, type TableHeaderProps } from "./header/table-header";
export { ColumnMenu, type ColumnMenuProps } from "./header/column-menu";
export {
  ColumnVisibility,
  type ColumnVisibilityProps,
} from "./header/column-visibility";

// ─── Rows ───────────────────────────────────────────────────────────────────
export { DataRow, type DataRowProps } from "./rows/data-row";
export {
  SelectionCell,
  SelectionAnchorProvider,
  type SelectionCellProps,
} from "./rows/selection-cell";

// ─── Footer ─────────────────────────────────────────────────────────────────
export {
  Pagination,
  DEFAULT_PAGE_SIZE_OPTIONS,
  type PaginationProps,
} from "./footer/pagination";
export {
  BulkActionBar,
  type BulkActionBarProps,
} from "./footer/bulk-action-bar";

// ─── Filters ────────────────────────────────────────────────────────────────
export {
  OPERATORS_BY_TYPE,
  getOperatorsForType,
  getOperatorsForColumn,
  getOperatorById,
  type OperatorDef,
  type OperatorId,
  type OperatorValueShape,
  type ColumnWithCustomOperators,
} from "./filters/operators";
export {
  FilterInput,
  type FilterInputProps,
  type ActiveFilter,
} from "./filters/filter-input";
export { FilterBar, type FilterBarProps } from "./filters/filter-bar";

// ─── Export writers ─────────────────────────────────────────────────────────
export { toCsv, CSV_MIME_TYPE, type ToCsvOptions } from "./export/to-csv";
export { toXlsx, XLSX_MIME_TYPE, type ToXlsxOptions } from "./export/to-xlsx";
export { toPdf, PDF_MIME_TYPE, type ToPdfOptions } from "./export/to-pdf";
export {
  ExportMenu,
  type ExportMenuProps,
  type ExportFormat,
} from "./export/export-menu";

// ─── States ─────────────────────────────────────────────────────────────────
export { EmptyTable, type EmptyTableProps } from "./states/empty-table";
export {
  LoadingTable,
  DEFAULT_LOADING_ROW_COUNT,
  type LoadingTableProps,
} from "./states/loading-table";
export { ErrorTable, type ErrorTableProps } from "./states/error-table";
